import { CHARACTER_ACTIONS } from "@/character/actions";
import { normalizeGridTuning, type GridTuning } from "@/character/draft";
import { resolveSheetLayout, sheetLayoutFromImage } from "@/character/grid";
import type { CharacterProfile } from "@/character/storage";
import { startRowLoop } from "@/character/sheet-preview-canvas";

const IDLE_ROW = CHARACTER_ACTIONS.find((a) => a.key === "idle")?.row ?? 0;
const DEFAULT_PREVIEW_SIZE = 160;

export type CharacterIdlePreviewOptions = {
  displaySizePx?: number;
};

export function mountCharacterIdlePreview(
  host: HTMLElement,
  profile: CharacterProfile,
  options?: CharacterIdlePreviewOptions,
): () => void {
  const previewSize = options?.displaySizePx ?? DEFAULT_PREVIEW_SIZE;
  host.innerHTML = `
    <div class="char-preview-stage">
      <canvas class="char-preview-canvas" role="img" aria-label="ตัวละคร idle"></canvas>
    </div>
  `;

  const canvas = host.querySelector<HTMLCanvasElement>(".char-preview-canvas");
  let stopLoop: (() => void) | undefined;

  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    if (!canvas) return;
    const layout = sheetLayoutFromImage(img);
    canvas.style.width = `${previewSize}px`;
    canvas.style.height = `${previewSize}px`;
    stopLoop = startRowLoop(canvas, img, layout, IDLE_ROW);
  };
  img.onerror = () => {
    host.querySelector(".char-preview-stage")?.append(
      Object.assign(document.createElement("p"), {
        className: "hint",
        textContent: "โหลดตัวละครไม่สำเร็จ",
      }),
    );
  };
  img.src = profile.spritesheetDataUrl;

  return () => {
    stopLoop?.();
    host.replaceChildren();
  };
}

/** Motion loop on upload sheet (create flow) using current grid tuning. */
export function mountDraftMotionPreview(
  host: HTMLElement,
  sheetDataUrl: string,
  gridTuning: GridTuning,
  row: number,
  motionLabel = "ตัวละคร",
  options?: CharacterIdlePreviewOptions,
): () => void {
  const previewSize = options?.displaySizePx ?? DEFAULT_PREVIEW_SIZE;
  const tuning = normalizeGridTuning(gridTuning);
  host.innerHTML = `
    <div class="char-preview-stage char-preview-stage--create">
      <canvas class="char-preview-canvas" role="img" aria-label="${motionLabel}"></canvas>
    </div>
  `;

  const canvas = host.querySelector<HTMLCanvasElement>(".char-preview-canvas");
  let stopLoop: (() => void) | undefined;

  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    if (!canvas) return;
    const layout = resolveSheetLayout(img, tuning);
    canvas.style.width = `${previewSize}px`;
    canvas.style.height = `${previewSize}px`;
    stopLoop = startRowLoop(
      canvas,
      img,
      layout,
      row,
      undefined,
      "loop",
      undefined,
      tuning,
    );
  };
  img.onerror = () => {
    host.querySelector(".char-preview-stage")?.append(
      Object.assign(document.createElement("p"), {
        className: "hint",
        textContent: "โหลดตัวละครไม่สำเร็จ",
      }),
    );
  };
  img.src = sheetDataUrl;

  return () => {
    stopLoop?.();
    host.replaceChildren();
  };
}

export function mountDraftIdlePreview(
  host: HTMLElement,
  sheetDataUrl: string,
  gridTuning: GridTuning,
  options?: CharacterIdlePreviewOptions,
): () => void {
  return mountDraftMotionPreview(host, sheetDataUrl, gridTuning, IDLE_ROW, "ตัวละคร idle", options);
}
